import { showError } from '@qodana/blocks/lib/services'
import { BaseQueryFn, FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { createApi, FetchArgs, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { FetchErrorNotification } from '../../components/FetchErrorNotification/FetchErrorNotification'
import { QueryErrorAlertType } from '../../config/runtime'

import { prepareClientVersionHeaders } from './clientVersionHeader'

type APIErrorMessage = {
	name: string
	details: string
}

function createDataApi(
	baseUrl: string,
	queryReportAlertType: QueryErrorAlertType,
	suppressedErrors: string[],
) {
	function isSuppressedError(error: FetchBaseQueryError) {
		try {
			return suppressedErrors.includes((error.data as APIErrorMessage).name)
		} catch {
			return false
		}
	}

	const baseQuery = fetchBaseQuery({
		baseUrl,
		credentials: 'include',
		prepareHeaders: prepareClientVersionHeaders,
		responseHandler: 'content-type',
	})

	const baseQueryWithAlertingService: BaseQueryFn<
		string | FetchArgs,
		unknown,
		FetchBaseQueryError
	> = async (args, api, extraOptions) => {
		const result = await baseQuery(args, api, extraOptions)
		if (result.error && !isSuppressedError(result.error)) {
			switch (queryReportAlertType) {
				case 'ui':
					showError(
						<FetchErrorNotification
							status={result.error.status}
							data={result.error.data}
							url={result.meta?.request.url}
						/>,
					)
					break
				case 'console':
					console.error(result.error)
					break
			}
		}

		return result
	}

	return createApi({
		reducerPath: 'dataApi',
		baseQuery: baseQueryWithAlertingService,
		tagTypes: [
			'Timeline',
			'Project',
			'ProjectToken',
			'Organization',
			'OrganizationUser',
			'Report',
			'Misc',
			'User',
			'UserAgreement',
			'Team',
			'TeamUser',
			'TeamPermission',
			'OrganizationPermission',
			'Logs',
			'ProjectBranches',
			'ProjectSetupState',
			'OrganizationVCSAccountList',
			'OrganizationProjectList',
			'OrganizationToken',
			'SavedFilters',
			'OrganizationProjectToken',
			'OrganizationSettings',
			'GlobalConfiguration',
			'GlobalConfigurationToken',
			'GlobalConfigurationProjects',
			'ViewedProblems',
		],
		endpoints: (builder) => ({
			getReportFiles: builder.query<
				{ files: { file: string; url: string; sizeBytes?: number | null }[] },
				{ reportId: string; files: string[] }
			>({
				query: ({ reportId, files }) => ({
					url: `reports/${reportId}/files`,
					params: { paths: files.join(',') },
				}),
			}),
		}),
	})
}

let _dataApi: ReturnType<typeof createDataApi>

export function initializeDataApi(
	baseUrl: string,
	queryReportAlertType: QueryErrorAlertType,
	suppressedErrors: string[],
) {
	_dataApi = createDataApi(baseUrl, queryReportAlertType, suppressedErrors)
}

export default function getDataApi() {
	if (typeof _dataApi === 'undefined') {
		throw new Error('DataApi not initialized yet!')
	}

	return _dataApi
}
